import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Loader2, Plus, Edit3, Trash2, Star, X, Save, Search, Newspaper, Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Changelog Manager ──────────────────────────────────────────
const ChangelogManager = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, version, title, body, highlights}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/changelog`);
      setEntries(res.data.entries || []);
    } catch (err) {
      toast.error('Failed to load changelog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    version: '', title: '', body: '', highlights: [{ title: '', body: '' }],
  });

  const startEdit = (e) => setEditing({
    id: e.id, version: e.version, title: e.title, body: e.body || '',
    highlights: (e.highlights && e.highlights.length) ? e.highlights : [{ title: '', body: '' }],
  });

  const save = async () => {
    if (!editing.version.trim() || !editing.title.trim()) {
      toast.error('Version and title are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        version: editing.version,
        title: editing.title,
        body: editing.body,
        highlights: editing.highlights.filter(h => h.title || h.body),
      };
      if (editing.id) {
        await axios.patch(`${API}/admin/changelog/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/changelog`, payload);
        toast.success('Created');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Soft-delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/changelog/${id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const updateHighlight = (idx, key, val) => {
    setEditing(e => {
      const next = [...e.highlights];
      next[idx] = { ...next[idx], [key]: val };
      return { ...e, highlights: next };
    });
  };

  return (
    <div data-testid="changelog-manager">
      <div className="flex items-center justify-between border-b border-[var(--f500-line)] pb-4 mb-6">
        <div>
          <p className="f500-eyebrow mb-1">— Release Notes</p>
          <h2 className="f500-h2 text-xl flex items-center gap-2">
            <Newspaper className="w-5 h-5" strokeWidth={1.75} />
            Changelog
          </h2>
        </div>
        <button onClick={startNew} className="f500-btn f500-btn-primary" data-testid="changelog-new-btn">
          <Plus className="w-4 h-4" /> New Entry
        </button>
      </div>

      {/* Editor modal */}
      {editing && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="f500-card max-w-2xl w-full p-6 lg:p-8 max-h-[90vh] overflow-y-auto bg-white">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-[var(--f500-line)]">
              <h3 className="f500-h2 text-2xl">{editing.id ? 'Edit Entry' : 'New Entry'}</h3>
              <button onClick={() => setEditing(null)} className="text-[var(--f500-ink-3)] hover:text-[var(--f500-ink)]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="f500-eyebrow block mb-2">Version</label>
                <input
                  className="f500-input-boxed"
                  placeholder="v2.1.0"
                  value={editing.version}
                  onChange={(e) => setEditing({ ...editing, version: e.target.value })}
                  data-testid="changelog-version-input"
                />
              </div>
              <div>
                <label className="f500-eyebrow block mb-2">Title</label>
                <input
                  className="f500-input-boxed"
                  placeholder="What changed?"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="changelog-title-input"
                />
              </div>
              <div>
                <label className="f500-eyebrow block mb-2">Body (optional)</label>
                <textarea
                  className="f500-input-boxed min-h-[100px]"
                  placeholder="One-paragraph summary…"
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  data-testid="changelog-body-input"
                />
              </div>

              <div>
                <label className="f500-eyebrow block mb-2">Highlights</label>
                <div className="space-y-3">
                  {editing.highlights.map((h, i) => (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start">
                      <input
                        className="f500-input-boxed"
                        placeholder="Highlight title"
                        value={h.title}
                        onChange={(e) => updateHighlight(i, 'title', e.target.value)}
                      />
                      <textarea
                        className="f500-input-boxed sm:col-span-2 min-h-[44px]"
                        placeholder="Body"
                        value={h.body}
                        onChange={(e) => updateHighlight(i, 'body', e.target.value)}
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => setEditing(e => ({ ...e, highlights: [...e.highlights, { title: '', body: '' }] }))}
                    className="text-xs font-medium text-[var(--f500-blue)] hover:underline"
                  >
                    + Add another highlight
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-[var(--f500-line)] flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="f500-btn f500-btn-secondary">Cancel</button>
              <button onClick={save} disabled={saving} className="f500-btn f500-btn-primary" data-testid="changelog-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="f500-card p-6 flex items-center gap-2 text-[var(--f500-ink-2)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : entries.length === 0 ? (
        <div className="f500-card p-12 text-center text-[var(--f500-ink-2)]">
          No entries yet. Click <strong>New Entry</strong> to publish your first changelog item.
        </div>
      ) : (
        <div className="border border-[var(--f500-line)]">
          {entries.map((e, idx) => (
            <div
              key={e.id}
              className={`p-5 bg-white ${idx > 0 ? 'border-t border-[var(--f500-line)]' : ''} ${!e.is_active ? 'opacity-60' : ''}`}
              data-testid={`changelog-row-${e.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="f500-mono text-xs text-[var(--f500-ink-3)]">{e.version}</span>
                    {!e.is_active && <span className="f500-badge">Hidden</span>}
                  </div>
                  <h3 className="f500-h3 text-base">{e.title}</h3>
                  <p className="text-xs text-[var(--f500-ink-3)] mt-1" style={{ fontFamily: 'var(--sf-text)' }}>
                    Published {new Date(e.published_at).toLocaleDateString()} · {e.highlights?.length || 0} highlights
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(e)} className="f500-btn f500-btn-secondary text-xs">
                    <Edit3 className="w-3 h-3" />
                  </button>
                  {e.is_active && (
                    <button onClick={() => remove(e.id)} className="f500-btn f500-btn-secondary text-xs">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Featured Creator Manager ────────────────────────────────────
const FeaturedCreatorManager = () => {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [picking, setPicking] = useState(null); // user being staged
  const [bio, setBio] = useState('');
  const [headline, setHeadline] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cur, hist] = await Promise.all([
        axios.get(`${API}/featured-creator`),
        axios.get(`${API}/admin/featured-creator`),
      ]);
      setCurrent(cur.data?.featured || null);
      setHistory(hist.data?.history || []);
    } catch {
      toast.error('Failed to load featured creator');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const search = async () => {
    if (!searchQ.trim()) {
      setResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/users`, { params: { q: searchQ.trim(), limit: 10 } });
      setResults(res.data?.users || []);
    } catch {
      toast.error('User search failed');
    }
  };

  const stage = (u) => {
    setPicking(u);
    setBio('');
    setHeadline('');
    setImgUrl('');
  };

  const publish = async () => {
    if (!picking) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/featured-creator`, {
        user_id: picking.id,
        bio_override: bio,
        headline,
        feature_image_url: imgUrl,
      });
      toast.success(`Featured ${picking.name}`);
      setPicking(null);
      setSearchQ('');
      setResults([]);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear current featured creator?')) return;
    try {
      await axios.delete(`${API}/admin/featured-creator`);
      toast.success('Cleared');
      loadAll();
    } catch {
      toast.error('Failed to clear');
    }
  };

  return (
    <div data-testid="featured-creator-manager" className="mt-12">
      <div className="flex items-center justify-between border-b border-[var(--f500-line)] pb-4 mb-6">
        <div>
          <p className="f500-eyebrow mb-1">— Top Creator of the Week</p>
          <h2 className="f500-h2 text-xl flex items-center gap-2">
            <Star className="w-5 h-5" strokeWidth={1.75} fill="currentColor" />
            Featured Creator
          </h2>
        </div>
        {current && (
          <button onClick={clear} className="f500-btn f500-btn-secondary text-xs" data-testid="featured-clear-btn">
            <X className="w-3 h-3" /> Clear current
          </button>
        )}
      </div>

      {loading ? (
        <div className="f500-card p-6 flex items-center gap-2 text-[var(--f500-ink-2)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Current pick */}
          {current && current.creator && (
            <div className="f500-card p-5 mb-6" data-testid="featured-current">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[var(--f500-ink)] flex items-center justify-center text-white f500-h2 text-xl">
                  {(current.creator.name || 'S').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="f500-eyebrow mb-1">Currently Featured</p>
                  <h3 className="f500-h3 text-lg">{current.creator.name}</h3>
                  <p className="text-xs text-[var(--f500-ink-2)]" style={{ fontFamily: 'var(--sf-text)' }}>
                    {current.creator.university || '—'} · {current.creator.public_recordings} public {current.creator.public_recordings === 1 ? 'recording' : 'recordings'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* User search + stage */}
          <div className="f500-card p-5 mb-6">
            <p className="f500-eyebrow mb-3">Pick a new creator</p>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--f500-ink-3)]" />
                <input
                  className="f500-input-boxed pl-10"
                  placeholder="Search by name or email…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && search()}
                  data-testid="featured-search-input"
                />
              </div>
              <button onClick={search} className="f500-btn f500-btn-secondary" data-testid="featured-search-btn">
                Search
              </button>
            </div>

            {results.length > 0 && (
              <div className="border border-[var(--f500-line)]">
                {results.map((u, idx) => (
                  <div
                    key={u.id}
                    className={`p-3 flex items-center gap-3 ${idx > 0 ? 'border-t border-[var(--f500-line)]' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--f500-ink)]" style={{ fontFamily: 'var(--sf-text)' }}>{u.name}</p>
                      <p className="text-xs text-[var(--f500-ink-3)] truncate">{u.email} · {u.university || u.institution || '—'}</p>
                    </div>
                    <button onClick={() => stage(u)} className="f500-btn f500-btn-primary text-xs">
                      Stage
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Stage form */}
            {picking && (
              <div className="mt-6 pt-6 border-t border-[var(--f500-line)] space-y-3" data-testid="featured-stage-form">
                <p className="text-sm font-semibold" style={{ fontFamily: 'var(--sf-text)' }}>
                  Staging: <span className="text-[var(--f500-blue)]">{picking.name}</span>
                </p>
                <div>
                  <label className="f500-eyebrow block mb-1">Headline (optional)</label>
                  <input
                    className="f500-input-boxed"
                    placeholder='e.g. "From struggling student to top scorer"'
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    maxLength={120}
                  />
                </div>
                <div>
                  <label className="f500-eyebrow block mb-1">Bio override (optional, ~60 words)</label>
                  <textarea
                    className="f500-input-boxed min-h-[80px]"
                    placeholder="Custom bio for the spotlight. Leave empty to use the user's default bio."
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    maxLength={400}
                  />
                </div>
                <div>
                  <label className="f500-eyebrow block mb-1">Feature image URL (optional)</label>
                  <input
                    className="f500-input-boxed"
                    placeholder="https://…"
                    value={imgUrl}
                    onChange={(e) => setImgUrl(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setPicking(null)} className="f500-btn f500-btn-secondary">Cancel</button>
                  <button onClick={publish} disabled={saving} className="f500-btn f500-btn-primary" data-testid="featured-publish-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                    Publish
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div>
              <p className="f500-eyebrow mb-3">— Past Picks</p>
              <div className="border border-[var(--f500-line)]">
                {history.slice(0, 10).map((h, idx) => (
                  <div key={h.id} className={`p-3 flex items-center gap-3 ${idx > 0 ? 'border-t border-[var(--f500-line)]' : ''} ${h.is_active ? 'bg-[var(--f500-paper-2)]' : ''}`}>
                    <Users className="w-3.5 h-3.5 text-[var(--f500-ink-3)]" strokeWidth={1.75} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" style={{ fontFamily: 'var(--sf-text)' }}>
                        <span className="font-semibold">{h.creator?.name || '(deleted user)'}</span>
                        {' '}<span className="text-[var(--f500-ink-3)]">· {new Date(h.featured_at).toLocaleDateString()}</span>
                      </p>
                    </div>
                    {h.is_active && <span className="f500-badge f500-badge-blue text-[9px]">Active</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Combined Tab ────────────────────────────────────────────────
const ContentTab = () => {
  return (
    <div className="space-y-4" data-testid="admin-content-tab">
      <ChangelogManager />
      <FeaturedCreatorManager />
    </div>
  );
};

export default ContentTab;
export { ChangelogManager, FeaturedCreatorManager };
